import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { catalogs, type Locale } from "./catalogs";
import { translateMessage, translatePlural, type TranslationParams } from "./translation";
import { telemetry } from "../telemetry/telemetry";

type I18nContextValue = Readonly<{
  locale: Locale;
  setLocale(locale: Locale): void;
  t(key: string, params?: TranslationParams): string;
  tp(key: string, count: number, params?: TranslationParams): string;
  formatDate(value: string): string;
  formatMonth(value: string): string;
  formatCurrency(value: string, currency: string): string;
  formatNumber(value: number): string;
  formatQuantity(value: number, unit: string): string;
}>;

const resolveInitialLocale = (): Locale => {
  const saved = window.localStorage.getItem("nave.locale");
  if (saved === "pt-BR" || saved === "en" || saved === "es") return saved;
  const browser = window.navigator.language.toLowerCase();
  if (browser.startsWith("es")) return "es";
  if (browser.startsWith("en")) return "en";
  return "pt-BR";
};

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

export const I18nProvider = ({ children }: { children?: ReactNode }) => {
  const [locale, setLocaleState] = useState<Locale>(resolveInitialLocale);
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo<I18nContextValue>(() => {
    const catalog = catalogs[locale];
    const dateFormatter = new Intl.DateTimeFormat(locale, { dateStyle: "medium" });
    const calendarDateFormatter = new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeZone: "UTC" });
    const monthFormatter = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric", timeZone: "UTC" });
    const numberFormatter = new Intl.NumberFormat(locale);
    const pluralRules = new Intl.PluralRules(locale);
    const currencyFormatters = new Map<string, Intl.NumberFormat>();

    const currencyFormatter = (currency: string): Intl.NumberFormat => {
      const existing = currencyFormatters.get(currency);
      if (existing !== undefined) return existing;
      const created = new Intl.NumberFormat(locale, { style: "currency", currency });
      currencyFormatters.set(currency, created);
      return created;
    };

    return {
      locale,
      setLocale: (next) => {
        window.localStorage.setItem("nave.locale", next);
        setLocaleState(next);
        telemetry.emit({ name: "locale.changed", outcome: "success" });
      },
      t: (key, params) => translateMessage(catalog, key, params),
      tp: (key, count, params) => translatePlural(catalog, locale, key, count, params, pluralRules),
      formatDate: (input) => (/^\d{4}-\d{2}-\d{2}(?:T00:00:00(?:\.000)?Z)?$/u.test(input) ? calendarDateFormatter : dateFormatter).format(new Date(input)),
      formatMonth: (input) => monthFormatter.format(new Date(input)),
      formatCurrency: (input, currency) => currencyFormatter(currency).format(Number(input)),
      formatNumber: (input) => numberFormatter.format(input),
      formatQuantity: (input, unit) => {
        if(unit==="bytes") {
          const exponent=input>=1e9?3:input>=1e6?2:input>=1e3?1:0;
          const units=["byte","kilobyte","megabyte","gigabyte"] as const;
          return new Intl.NumberFormat(locale,{style:"unit",unit:units[exponent],maximumFractionDigits:2}).format(input/(1000**exponent));
        }
        if(["assets","credits"].includes(unit))return `${numberFormatter.format(input)} ${translatePlural(catalog,locale,`usage.unit.${unit}`,input,{},pluralRules)}`;
        const localizedUnit = unit === "analysis"
          ? translatePlural(catalog, locale, "usage.unit.analysis", input, {}, pluralRules)
          : unit;
        return `${numberFormatter.format(input)} ${localizedUnit}`;
      },
    };
  }, [locale]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = (): I18nContextValue => {
  const value = useContext(I18nContext);
  if (value === undefined) throw new Error("I18nProvider missing");
  return value;
};
